import {
  HttpErrorResponse,
  HttpEvent,
  HttpHandler,
  HttpInterceptor,
  HttpRequest,
} from '@angular/common/http';
import { Injectable } from '@angular/core';
import {
  BehaviorSubject, catchError, combineLatest, delayWhen, EMPTY, finalize, first, iif, map, Observable,
  ObservableInput, of, retry, switchMap, take, tap
} from 'rxjs';
import { TokenService } from '../services/token.service';
import { Router } from '@angular/router';
import { AuthService } from '~services/auth.service';
import { IToken } from '~models/interfaces/token.interface';
import { ILoginResponseData } from '~models/interfaces/login.interface';

@Injectable(
  {
    providedIn: "root"
  }
)
export class AuthInterceptor implements HttpInterceptor {
  private holdRequests$: BehaviorSubject<boolean> = new BehaviorSubject(false);

  constructor(
    private tokenService: TokenService,
    private authService: AuthService,
    private router: Router) {
  }

  public intercept(
    req: HttpRequest<unknown>,
    next: HttpHandler
  ): Observable<HttpEvent<unknown>> {

    return combineLatest([
      // запрос req ждет разблокировки очереди, но запрос refresh пропускаем без задержки
      this.holdRequests$.pipe(first((val: boolean) => !val || req.url.includes("/api/auth/refresh"))),
      // используем последний access-токен
      this.tokenService.getAccessToken().pipe(
        map((data: IToken | null) => {
          const token = data?.token ?? '';
          if (token !== '') {
            req = req.clone({ setHeaders: { Authorization: `Bearer ${token}` } });
          }
        }))
    ]).pipe(
      take(1),
      switchMap(() => next.handle(req)),
      retry(
        {
          count: 1, // одна попытка на повтор запроса
          delay: error => {
            return of(error)
              .pipe(
                delayWhen(() => this.tryRefresh(error, req)),
              );
          }
        }
      )
    );
  }

  /** Попытаться обновить токены */
  private tryRefresh(err: unknown, req: HttpRequest<unknown>): ObservableInput<unknown> {
    if (err instanceof HttpErrorResponse && err.status === 401 && !req.url.includes("/api/auth/login") && !req.url.includes("/api/auth/refresh")) {
      return iif(
        () => this.holdRequests$.value, // проверяем, что никто не рефрешится
        this.holdRequests$.pipe(
          first((val: boolean) => !val), // иначе просто дождаться пока очередь не разблокируется
        ),
        of(true).pipe(
          tap(() => this.holdRequests$.next(true)), // блокируем очередь запросов
          switchMap(() => this.tokenService.getRefreshToken()),
          take(1),
          switchMap((refreshToken: IToken | null) => this.authService.refresh({ refreshToken: refreshToken?.token ?? '' })),
          tap((authData: ILoginResponseData) =>
            this.tokenService.setTokens(authData)
          ),
          catchError((err) => {
            this.handleRefreshError(err);
            return EMPTY;
          }),
          finalize(() => this.holdRequests$.next(false)), // снимаем блокировку очереди
        )
      );
    }
    throw new Error(JSON.stringify(err)); // если ошибка не 401, то пробросить ее дальше
  }

  /** Обработать ошибку обновления токенов */
  private handleRefreshError(err: unknown): void {
    console.error("Refresh token error", err);
    this.tokenService.dropTokens();
    this.router.navigate(['/auth']);
  }

}
